import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { updateConfig as globalUpdateConfig } from "../../../store/slices/Global";
import {
  user,
  useGetPlaceholderListDataQuery,
  useUpdatePlaceholderHeadDataMutation,
  useUpdatePlaceholderStatusMutation,
} from "../../../store/queries/User";
import { deletePlaceholder } from "./api";
import { toast } from "react-toastify";
import { current } from "@reduxjs/toolkit";
import { getPlaceholderEditData, updateConfig } from "../../../store/slices/User/Placeholder/placeholderSlice";

const usePlaceholder = () => {
  let paginationOptions = [
    { label: "10 Per page", value: 10 },
    { label: "20 Per page", value: 20 },
    { label: "30 Per page", value: 30 },
  ];
 
  const dispatch = useDispatch();
  const placeholderState = useSelector((state) => state.placeholder);
  const { selectedId } = useSelector((state) => state.placeholder);
  const { showEditModal } = useSelector((state) => state.global);
  const {
    data: placeholderList = {},
    isLoading,
    isFetching,
    refetch,
  } = useGetPlaceholderListDataQuery({
    page_size: placeholderState?.currentPageSize,
    page: placeholderState?.currentPage,
    sort: placeholderState?.sortBy,
    sort_order: placeholderState?.sortOrder,
    search: placeholderState?.search,
  });
  const [updatePlaceholderFields] = useUpdatePlaceholderHeadDataMutation();
  const [updatePlaceholderStatus] = useUpdatePlaceholderStatusMutation();
  const handleCreateClick = () => {
    dispatch(
      updateConfig((state) => {
        state.showCreateModal = !state.showCreateModal;
        state.imagePreview = "";
        state.imageName = "";
        state.arabicImagePreview = "";
        state.arabicImageName = "";
        state.isEdit = false;
        state.editPlaceholderScreenData = {};
      })
    );
  };
  useEffect(() => {
    if (placeholderState.clearSelection) {
      dispatch(
        updateConfig((state) => {
          state.clearSelection = false;
        })
      );
    }
    // eslint-disable-next-line
  }, [placeholderState.clearSelection]);

  const handleSearch = (e) => {
    dispatch(
      updateConfig((state) => {
        state.search = e.target.value;
        state.currentPage = 1;
      })
    );
  };
  const handleClearClick = () => {
    dispatch(updateConfig((state) => (state.search = "")));
  };

  const handleEditColumnsClick = () => {
    dispatch(
      globalUpdateConfig((state) => {
        state.showEditModal = !state.showEditModal;
      })
    );
  };

  const handleToggle = (e, data) => {
    const newStatus = data.status === 1 ? 2 : 1;
    let body = {
      search_id: data?.id,
      status: newStatus,
    };
    updatePlaceholderStatus(body);
    dispatch(
      user.util.updateQueryData(
        "getPlaceholderListData",
        {
          page_size: placeholderState?.currentPageSize,
          page: placeholderState?.currentPage,
          sort: placeholderState?.sortBy,
          sort_order: placeholderState?.sortOrder,
          search: placeholderState?.search,
        },
        (cacheEntry) => {
          let currentCache = { ...current(cacheEntry) };
          const newResult = currentCache.data.result?.map((res) =>
            res.id === data?.id
              ? {
                  ...res,
                  status: newStatus,
                }
              : res
          );
          currentCache = {
            ...currentCache,
            data: {
              ...currentCache.data,
              result: newResult,
            },
          };
          return currentCache;
        }
      )
    );
  };

  const getRow = (field, data) => {
    const rows = {
      common: (field, data) => <p className="pro-mb-0">{data[field]}</p>,
      start_date: (field, data) => <p className="pro-mb-0">{data.start_time}</p>,
      end_date: (field, data) => <p className="pro-mb-0">{data.end_time}</p>,
      status: (field, data) => {
        const isChecked = data?.status === 1;
        return (
          <div className="form-check form-switch ">
            <input
              className="form-check-input cursor-pointer"
              type="checkbox"
              role="switch"
              id="flexSwitchCheckDefault"
              checked={isChecked}
              onChange={(e) => handleToggle(e, data)}
            />
          </div>
        );
      },
    };
    return rows?.[field]?.(field, data) ?? rows["common"](field, data);
  };

  const handleSort = (label) => {
    if (placeholderState.sortBy === label) {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortOrder = placeholderState.sortOrder === "asc" ? "desc" : "asc";
        })
      );
    } else {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortBy = label;
          state.sortOrder = "asc";
        })
      );
    }
  };

  const handleDeleteModal = (e) => {
    dispatch(
      updateConfig((state) => {
        state.showDeleteModal = !state.showDeleteModal;
        state.selectedId = e?.[0];
      })
    );
  };

  const handleDelete = async () => {
    const response = await deletePlaceholder(selectedId);
    if (response && response.status === 200) {
      toast.success(`Deleted successfully.`);
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.search="";
        })
      );
      setTimeout(() => {
        refetch();
      }, 0);
    } else {
      toast.error(`Something went wrong !`);
    }
    handleDeleteModal();
    dispatch(
      updateConfig((state) => {
        state.clearSelection = true;
      })
    );
  };

  const handlePagination = (page) => {
    dispatch(
      updateConfig((state) => {
        state.currentPage = page;
      })
    );
  };
  const handlePageSize = ({ value: page_size }) => {
    dispatch(
      updateConfig((state) => {
        state.currentPageSize = page_size;
        state.currentPage = 1;
      })
    );
  };
 
  const handleEditAction = (e) => {
    dispatch(
      updateConfig((state) => {
        state.showCreateModal = true;
        state.selectedId = e?.[0];
        state.isEdit = true;
      })
    );
    dispatch(getPlaceholderEditData(e?.[0]));
  };

  return {
    isLoading,
    isFetching,
    placeholderList,
    showEditModal,
    placeholderState,
    paginationOptions,
    handleEditColumnsClick,
    updatePlaceholderFields,
    handleDeleteModal,
    handleCreateClick,
    handlePagination,
    handleClearClick,
    handleEditAction,
    handlePageSize,
    handleSearch,
    handleSort,
    handleDelete,
    refetch,
    getRow,
  };
};

export default usePlaceholder;
